/** Statuts du matériel — jeu fermé partagé par tout le module (RM-PIN-013). */
export const ASSET_STATUSES = [
  'Disponible',
  'Assigné',
  'Disponible - À vérifier',
  'En réparation',
  'En commande',
  'Hors service',
  'Perdu/Volé',
] as const;

export type AssetStatus = (typeof ASSET_STATUSES)[number];

/** Statuts comptés dans le parc actif (RM-PIN-100). */
export const ACTIVE_ASSET_STATUSES: readonly AssetStatus[] = [
  'Disponible',
  'Assigné',
  'En réparation',
  'En commande',
  'Disponible - À vérifier',
];

export type HolderType = 'collab' | 'lieu';

export interface AuditEntry {
  readonly date: string;
  readonly label: string;
}

/**
 * Photo attachée à une fiche matériel (RM-PIN-122).
 *
 * `url` porte aujourd'hui une data URL produite par le navigateur : le module n'a pas encore de
 * stockage de fichiers. En production, le téléversement renvoie une URL d'objet et seul ce champ
 * change — les écrans ne connaissent que ce contrat.
 */
export interface AssetPhoto {
  readonly id: string;
  readonly fileName: string;
  readonly url: string;
  /** Type MIME retenu, à revalider hors de l'UI : il porte la contrainte de format de RM-PIN-122. */
  readonly mimeType: string;
  readonly sizeBytes: number;
  /**
   * Photo principale, affichée dans les listes et sur l'étiquette d'inventaire (RM-PIN-122).
   * Une fiche n'en porte qu'une : la première ajoutée, jusqu'à désignation explicite d'une autre.
   */
  readonly isPrimary: boolean;
}

/** RM-PIN-122 — une fiche accepte au plus 5 photos. */
export const ASSET_PHOTO_MAX_COUNT = 5;

/** RM-PIN-122 — 5 Mo maximum par fichier. */
export const ASSET_PHOTO_MAX_BYTES = 5 * 1024 * 1024;

/** RM-PIN-122 — jeu fermé de formats acceptés. */
export const ASSET_PHOTO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** Valeur de l'attribut `accept` du sélecteur de fichiers, dérivée des formats de RM-PIN-122. */
export const ASSET_PHOTO_ACCEPT = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';

/** ERR-PIN-122 — message exact de la spec, format et volume confondus dans une seule erreur. */
export const ERR_PIN_122 =
  'Photo refusée : format non supporté ou fichier supérieur à 5 Mo. Formats acceptés : jpg, png, webp.';

/**
 * RM-PIN-122 — format **et** volume. La spec réunit les deux causes sous un seul code d'erreur
 * (ERR-PIN-122), d'où un unique prédicat plutôt que deux contrôles distincts.
 */
export function isAcceptedAssetPhoto(file: { readonly type: string; readonly size: number }): boolean {
  return (
    (ASSET_PHOTO_MIME_TYPES as readonly string[]).includes(file.type) &&
    file.size <= ASSET_PHOTO_MAX_BYTES
  );
}

/** Photo principale de la fiche, ou `null` si elle n'en porte aucune. */
export function primaryPhoto(asset: Asset): AssetPhoto | null {
  return asset.photos.find((photo) => photo.isPrimary) ?? asset.photos[0] ?? null;
}

export interface Asset {
  /**
   * Identité de la fiche. Une **chaîne** et non un entier : le backend attribue un GUID v7
   * (`Entity.NewId()`), et le jeu de démonstration s'y aligne pour que les écrans n'aient pas deux
   * formes d'identité à connaître selon la source de données.
   */
  readonly id: string;
  /**
   * Numéro d'inventaire interne, format `PIN-AAAA-NNNNN` (RM-PIN-120). Généré par l'application à
   * la création, immuable, jamais réutilisé même après réforme. C'est **l'identifiant physique de
   * référence** du matériel, celui qui figure sur l'étiquette — indépendant du numéro de série
   * fabricant. La maquette v5 ne l'affichait pas ; la spec en fait le seul identifiant obligatoire.
   */
  readonly inventoryNumber: string;
  /**
   * Numéro de série fabricant. Obligatoire seulement pour les catégories déclarées sérialisées
   * (RM-PIN-121), et unique sur tout le parc lorsqu'il est renseigné (RM-PIN-001).
   * `null` pour un matériel encore en commande, dont le numéro est saisi à la réception.
   */
  readonly serialNumber: string | null;
  readonly brand: string;
  readonly name: string;
  readonly category: string;
  readonly status: AssetStatus;
  /**
   * Détenteur **tel qu'il s'affiche**. Affectation exclusive : un collaborateur ou un lieu, jamais
   * les deux (RM-PIN-018). `null` signifie « en stock », et rien d'autre : un matériel affecté dont
   * le nom du détenteur n'a pas pu être résolu porte quand même un libellé, sinon la liste
   * l'annoncerait à tort comme disponible.
   */
  readonly holder: string | null;
  /**
   * Identité du collaborateur détenteur, seule donnée que le backend expose aujourd'hui
   * (`AssetRow.Holder`). La résolution du nom appartient au module Directory : tant que l'annuaire
   * n'est pas branché, `holder` porte un libellé dérivé de cet identifiant.
   */
  readonly holderId: string | null;
  readonly holderType: HolderType | null;
  readonly location: string;
  readonly purchaseDate: string;
  /** Durée de garantie en mois, saisie à la création. `null` si inconnue. */
  readonly warrantyMonths: number | null;
  /** Fin de garantie, calculée depuis la date d'achat et la durée (RM-PIN-008). `null` = inconnue. */
  readonly warrantyEnd: string | null;
  readonly value: number;
  readonly notes: string | null;
  readonly supplier: string;
  readonly assignedOn: string | null;
  /** Photos de la fiche, dont une principale (RM-PIN-122). Vide si aucune photo n'a été jointe. */
  readonly photos: readonly AssetPhoto[];
  readonly history: readonly AuditEntry[];
}

/** Raccourcis de statut utilisés dans les listes de la maquette. */
export function shortStatus(status: AssetStatus): string {
  return status === 'Disponible - À vérifier' ? 'À vérifier' : status;
}

/**
 * Classe de badge de la datatable du kit (`.uni-dt-badge--*`) par statut, alignée sur `statusDot`.
 * Les variantes « soft » posent un texte 600/700 sur une teinte 50 (fort contraste, AA respecté) —
 * contrairement aux `.uni-badge` couleur du kit, écartés pour l'AA.
 */
export function statusBadgeClass(status: AssetStatus): string {
  const variant: Record<AssetStatus, string> = {
    Assigné: 'success-soft',
    Disponible: 'info-soft',
    'Disponible - À vérifier': 'warning-soft',
    'En réparation': 'warning-soft',
    'En commande': 'info-soft',
    'Hors service': 'neutral',
    'Perdu/Volé': 'danger-soft',
  };
  return `uni-dt-badge uni-dt-badge--${variant[status]}`;
}

/** Couleur de pastille par statut, reprise de la maquette. */
export function statusDot(status: AssetStatus): string {
  const dots: Record<AssetStatus, string> = {
    Assigné: 'var(--status-success-default)',
    Disponible: 'var(--text-action)',
    'Disponible - À vérifier': 'var(--status-warning-default)',
    'En réparation': 'var(--status-warning-default)',
    'En commande': 'var(--app-uni-it-action-default)',
    'Hors service': 'var(--text-tertiary-2)',
    'Perdu/Volé': 'var(--status-danger-default)',
  };
  return dots[status];
}
